import { ref, watch, computed } from '@vue/composition-api';
import store from '@/store';
import { formatDateBr } from '@core/utils/filter';
import SLSItemService from '@/services/SistemaDKP/SLSItem.service';
import SLSEstoqueService from '@/services/SistemaDKP/SLSEstoque.service';
// Notification
import { useToast } from 'vue-toastification/composition';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';

export default function useUsersList(nameModule, onEstoqueLoaded) {
  // Use toast
  const toast = useToast();
  const sLSItemService = new SLSItemService();
  const estoqueService = new SLSEstoqueService();
  const refItemListTable = ref(null);

  // Table Handlers
  const tableColumns = [
    { key: 'item_id', label: 'Codigo', sortable: false },
    { key: 'item_descricao', label: 'Descrição', sortable: true },
    { key: 'item_situacao', label: 'Situação', sortable: true },
    // Bolinhas verde/vermelha (paridade com o sistema antigo): dá visibilidade
    // imediata de como o produto está configurado por tipo de pedido.
    { key: 'flagvendavel', label: 'Vendável', sortable: true },
    { key: 'flagdoavel', label: 'Doável', sortable: true },
    { key: 'flagcertificacao', label: 'Certificação', sortable: true },
    { key: 'estoque_info', label: 'Estoque', sortable: false },
    // Coleção substitui as antigas colunas Família Comercial/Material (campo colecao vem direto do sls_item).
    { key: 'colecao', label: 'Coleção', sortable: true },
    { key: 'grupo_estoque.grupo_estoque_descricao', label: 'Grupo Estoque', sortable: true },
    { key: 'actions', label: 'Ações', sortable: false },
  ];
  const perPage = ref(10);
  const totalItem = ref(0);
  const currentPage = ref(1);
  const perPageOptions = [10, 25, 50, 100];
  const searchQuery = ref('');
  const sortBy = ref('item_id');
  const isSortDirDesc = ref(false);
  const overlay = ref(true);
  const statusFilter = ref(null);
  // Filtro por flags do item (multi-seleção). Guarda as chaves das colunas flag.
  const flagsFilter = ref([]);
  const flagOptions = [
    { label: 'Customizável', value: 'flagcustomizado' },
    { label: 'Assessment', value: 'flagassesstment' },
    { label: 'Doável', value: 'flagdoavel' },
    { label: 'Vendável', value: 'flagvendavel' },
    { label: 'Certificação', value: 'flagcertificacao' },
    { label: 'Reservado', value: 'flagreservado' },
  ];

  const dataMeta = computed(() => {
    const localItemsCount = refItemListTable.value ? refItemListTable.value.localItems.length : 0;
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalItem.value,
    };
  });

  const refetchData = () => {
    refItemListTable.value.refresh();
  };

  watch([currentPage, perPage, searchQuery, statusFilter, flagsFilter], () => {
    refetchData();
  });

  const fetchItem = (ctx, callback) => {
    overlay.value = true;
    sLSItemService.findAll({
      q: searchQuery.value,
      perPage: perPage.value,
      page: currentPage.value,
      sortBy: sortBy.value,
      sortDesc: isSortDirDesc.value,
      flags: (flagsFilter.value || []).join(','),
      fullObject: true,
    }).then((response) => {
      const { data, total } = response;
      overlay.value = false;
      callback(data);
      perPage.value = response.perPage;
      currentPage.value = response.page;
      totalItem.value = total;

      const ids = (data || []).map(i => i.item_id).filter(Boolean);
      if (ids.length && typeof onEstoqueLoaded === 'function') {
        estoqueService.consultarEstoque(ids).then(map => {
          onEstoqueLoaded(map);
        });
      }
    })
      .catch((error) => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error fetching users list',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      });
  };

  const createOrUpdated = async (body) => {
    if (body.item_id) {
      await sLSItemService.update(body.item_id, body);
    } else {
      await sLSItemService.store(body);
    }
    refetchData();
  };

  const removerData = async (id) => {
    await sLSItemService.destroy(id);
    refetchData();
  };

  const resolveTypeFieldText = type => {
    if (type === 'VARCHAR') return 'Texto';
    if (type === 'BOOLEAN') return 'Verdadeiro/Falso';
    if (type === 'DATE') return 'Data';
    if (type === 'DATETIME') return 'Data e Hora';
    if (type === 'DECIMAL') return 'Monetário';
    if (type === 'TEXT') return 'Texto Longo';
    if (type === 'INTEGER' || type === 'INT') return 'Númerico';
    return 'Sem Tipo';
  };
  const resolveTypeFieldIcon = type => {
    if (type === 'VARCHAR') return 'tabler:abc';
    if (type === 'BOOLEAN') return 'Verdadeiro/Falso';
    if (type === 'DATE') return 'system-uicons:calendar-date';
    if (type === 'DATETIME') return 'formkit:datetime';
    if (type === 'TEXT') return 'mdi:text-long';
    if (type === 'DECIMAL') return 'tabler:decimal';
    if (type === 'DECIMAL' || type === 'INTEGER' || type === 'INT') return 'tabler:123';
    return 'nonicons:type-16';
  };

  const resolveItemStatusVariant = (status) => {
    if (status === 'PENDING') return 'light-warning';
    if (status === 'ATIVO') return 'light-success';
    if (status === 'INATIVO') return 'light-danger';
    return 'primary';
  };

  const resolverDefaultValues = () => {};

  return {
    fetchItem,
    tableColumns,
    perPage,
    currentPage,
    resolverDefaultValues,
    totalItem,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refItemListTable,
    overlay,
    resolveTypeFieldText,
    resolveTypeFieldIcon,
    resolveItemStatusVariant,
    refetchData,
    createOrUpdated,

    // Extra Filters
    statusFilter,
    flagsFilter,
    flagOptions,
    removerData,
  };
}
